// Turns an Exchange response into what the Console shows. Pure, so the tests
// run it on real responses. It knows no hub, intent or shape: hub names come
// from the response, a Thing's title comes from GET /v1/things, and a field's
// description comes from GET /v1/shapes, or from the Answer for a manifest
// shape (shapes.ts). See Design
// > Overview, "How answers render" and Design > Answer shapes.

import { manifestShapeFields, type ShapeCache } from "./shapes.ts";
import type { AnswerNode, HubResponse, JsonObject, ManifestShape, NotDispatched, QuestionResult, ResponseTag, ShapedAnswer } from "./types.ts";

export type AnswerResponse = Extract<HubResponse, { _tag: "Answer" }>;

export const shortHub = (hub: string | null): string => (hub ? hub.split("/").pop() ?? hub : "no hub");
export const answersOf = (q: QuestionResult): AnswerResponse[] => q.responses.filter((r): r is AnswerResponse => r._tag === "Answer");
export const tagsOf = (q: QuestionResult): ResponseTag[] => q.responses.map((r) => r._tag);

/** One charge per answer, in every question of a response, newest first, for this session's ledger. Prices are per million answers. */
export function chargesOf(res: { questions: QuestionResult[] }, at: number) {
  return res.questions.flatMap((q) => answersOf(q).map((a) => ({ type: "Charge" as const, what: q.text, hub: a.hub, amount: -(a.price / 1_000_000), at, answerId: a.answerId }))).reverse();
}

/** The ask that a PriceTooLow named, per million answers, for a lesson that says "raise to the ask". */
export function askOf(q: QuestionResult): number | null {
  const r = q.responses.find((x): x is Extract<HubResponse, { _tag: "PriceTooLow" }> => x._tag === "PriceTooLow");
  return r ? r.ask : null;
}

export type Headline =
  | { kind: "answer"; text: string }
  | { kind: "none"; text: string; sub: string };

type NoAnswerText = { text: string; sub: string };

/** What to say first when no hub answered. */
export function noAnswerHeadline(q: QuestionResult): NoAnswerText {
  const first = q.responses[0];
  if (!first) return { text: "No hub answered.", sub: "No hub was dispatched the question. You weren't charged." };
  const hub = shortHub(first.hub);
  switch (first._tag) {
    case "PriceTooLow": return { text: "No hub answered.", sub: `${hub} can answer, but its ask is above your bid. You weren't charged.` };
    case "MissingParameters": return { text: `${hub} needs one more detail.`, sub: "It read your question, but it doesn't say everything that it needs. You weren't charged." };
    case "OutOfScope": return first.hub === null
      ? { text: "No hub is about this subject.", sub: `${first.reason} You weren't charged.` }
      : { text: `${hub} declined.`, sub: "The question reached the hub, but the hub doesn't hold what it asks for. You weren't charged." };
    case "NotFound": return { text: `${hub} found nothing.`, sub: `${first.detail} You weren't charged.` };
    case "NotImplemented": return { text: `${hub} knows what you asked, but can't answer it yet.`, sub: "Its tool isn't built. You weren't charged." };
    case "HubError": return { text: `${hub} couldn't answer.`, sub: `${first.detail}${first.retryable ? " You can try again." : ""} You weren't charged.` };
    case "Answer": return { text: `${hub} answered.`, sub: "" };
  }
}

export type HubRow = {
  hub: string;
  outcome: ResponseTag | NotDispatched["reason"];
  intent: string | null;
  note: string;
  /** US dollars per million answers, when the row is priced. */
  ask: number | null;
  bid: number | null;
  score: number | null;
  threshold: number | null;
  dispatched: boolean;
};

/** One row per hub: dispatched hubs by what they returned, then the hubs that matched and weren't dispatched. */
export function hubRows(q: QuestionResult): HubRow[] {
  const rows: HubRow[] = q.responses.map((r) => {
    const hub = r.hub ?? "no hub";
    const base: Omit<HubRow, "outcome" | "note"> = { hub, intent: "intent" in r ? r.intent : null, ask: null, bid: null, score: null, threshold: null, dispatched: r.hub !== null };
    switch (r._tag) {
      case "Answer": return { ...base, outcome: r._tag, note: "Answered.", ask: r.price };
      case "PriceTooLow": return { ...base, outcome: r._tag, note: "This hub can answer, but it asks more than you bid.", ask: r.ask, bid: r.bid };
      case "MissingParameters": return { ...base, outcome: r._tag, note: `Needs ${r.missing.map((m) => m.name).join(", ")}.` };
      case "OutOfScope": return { ...base, outcome: r._tag, note: r.reason };
      case "NotFound": return { ...base, outcome: r._tag, note: r.detail };
      case "NotImplemented": return { ...base, outcome: r._tag, note: "Its tool isn't built yet." };
      case "HubError": return { ...base, outcome: r._tag, note: r.detail };
    }
  });
  for (const n of q.notDispatched) {
    const score = n.score ?? null;
    rows.push({ hub: n.hub, outcome: n.reason, intent: null, ask: null, bid: null, score, threshold: n.threshold ?? null, dispatched: false,
      note: n.reason === "BelowDispatchThreshold" ? "Its hub score is under its dispatch threshold, so it wasn't dispatched." : "It matched, but wasn't dispatched." });
  }
  return rows;
}

// ---- describing one answer

export type Fact = { label: string; value: string; description?: string };
export type AnswerView = {
  kind: "thing" | "assertion" | "collection";
  shape: string;
  title: string;
  /** The first number, when the answer is a measurement. */
  headline: Fact | null;
  facts: Fact[];
  about: string | null;
  members: { ref: string; title: string; detail: string[] }[];
};

const lastSegment = (s: string) => s.replace(/@v\d+$/, "").split("/").pop() ?? s;
const shapeName = (s: string) => lastSegment(s);
const isIdLike = (s: string) => /^[0-9A-Z]{20,}(@v\d+)?$/.test(s) || s.startsWith("wh:");

type CollectionNode = Extract<AnswerNode, { kind: "collection" }>;

/** The references that a collection holds. A new collection type fails to compile until it is listed. */
const nodeRefs = (n: CollectionNode): string[] => {
  switch (n.type) {
    case "Set": return n.members;
    case "List": return n.items;
    case "Arc": return [n.from, n.to];
    case "Bond": return [...n.ends];
    default: return n satisfies never;
  }
};

/** Every wref in an answer that a title could be fetched for. */
export function refsOf(a: ShapedAnswer): string[] {
  const refs = new Set<string>();
  const add = (r: string) => { if (!r.startsWith("_:")) refs.add(r); };
  add(a.root);
  for (const n of Object.values(a.nodes)) {
    switch (n.kind) {
      case "collection": nodeRefs(n).forEach(add); break;
      case "assertion": add(n.about); break;
      case "thing": break;
      default: n satisfies never;
    }
  }
  return [...refs];
}

// A local Thing is titled by its local ID, which the tool names, such as
// "apple" for "_:apple", or by its shape's name when AnswerBuilder numbered
// it ("_:n3"); any other local node is the answer itself.
const titleOf = (ref: string, a: ShapedAnswer, titles: Record<string, string>): string => {
  if (ref.startsWith("_:")) {
    const node = a.nodes[ref];
    if (node?.kind !== "thing") return "this answer";
    return /^_:n\d+$/.test(ref) ? shapeName(node.shape) : ref.slice(2);
  }
  return titles[ref] ?? a.things[ref]?.name?.split("/").pop() ?? lastSegment(ref);
};

// A node's data, for an existing Thing or a local Thing; empty for any other.
const dataOf = (ref: string, a: ShapedAnswer): JsonObject => {
  const node = a.nodes[ref];
  return node?.kind === "thing" ? node.data : (a.things[ref]?.data ?? {});
};

const factsOf = (data: JsonObject): Fact[] =>
  Object.entries(data)
    .filter(([, v]) => (typeof v === "string" && !isIdLike(v)) || typeof v === "number" || typeof v === "boolean")
    .map(([label, v]) => ({ label, value: String(v) }));

/** True when a title is only the last part of the Thing's name, which GET /v1/things uses when a hub declares no title for the shape. */
const untitled = (ref: string, titles: Record<string, string>) => {
  const t = titles[ref];
  return !t || t === lastSegment(ref);
};

const isDurableId = (v: unknown): v is string => typeof v === "string" && /^[0-9A-Z]{26,}$/.test(v);

/**
 * Names the members of a collection. A member with a title uses it. One without,
 * such as a Thing that relates two others, is named by the references that
 * differ between the members, with the other fields that differ beside it. See
 * Design > Overview, "How answers render".
 */
function membersOf(n: AnswerNode, a: ShapedAnswer, titles: Record<string, string>): AnswerView["members"] {
  if (n.kind !== "collection") return [];
  const refs = nodeRefs(n);
  const datas = refs.map((r) => dataOf(r, a));
  const varies = (key: string) => new Set(datas.map((d) => JSON.stringify(d[key]))).size > 1;
  return refs.map((ref, i) => {
    if (!untitled(ref, titles)) return { ref, title: titleOf(ref, a, titles), detail: [] };
    const data = datas[i] ?? {};
    const keys = Object.keys(data).filter(varies);
    const names = keys.map((k) => data[k]).filter(isDurableId).map((id) => titles[id]).filter((t): t is string => !!t);
    const detail = keys.map((k) => data[k]).filter((v): v is string | number => (typeof v === "string" && !isIdLike(v)) || typeof v === "number").map(String);
    return { ref, title: names.length > 0 ? names.join(" · ") : titleOf(ref, a, titles), detail };
  });
}

export function describeAnswer(a: ShapedAnswer, titles: Record<string, string>, shapes: ShapeCache = {}, carried: Record<string, ManifestShape> = {}): AnswerView {
  const node = a.nodes[a.root];
  if (!node) {
    const thing = a.things[a.root];
    return { kind: "thing", shape: thing ? shapeName(thing.shape) : "Thing", title: titleOf(a.root, a, titles), headline: null, facts: thing ? factsOf(thing.data) : [], about: null, members: [] };
  }
  if (node.kind === "collection") {
    return { kind: "collection", shape: node.type, title: `${node.type} of ${membersOf(node, a, titles).length}`, headline: null, facts: [], about: null, members: membersOf(node, a, titles) };
  }
  // With the shape's definition, each field has its description, and the
  // enum fields, such as a unit, follow the headline. A manifest shape's
  // comes with this answer, `carried`; a pinned ref's from GET /v1/shapes.
  const manifestShape = Object.hasOwn(carried, node.shape) ? carried[node.shape] : undefined;
  const fields = manifestShape ? manifestShapeFields(manifestShape) : (shapes[node.shape]?.fields ?? {});
  const described = (f: Fact): Fact => {
    const description = fields[f.label]?.description;
    return description ? { ...f, description } : f;
  };
  if (node.kind === "thing") {
    return { kind: "thing", shape: shapeName(node.shape), title: titleOf(a.root, a, titles), headline: null, facts: factsOf(node.data).map(described), about: null, members: [] };
  }
  const isEnum = (f: Fact) => (fields[f.label]?.enum ? 0 : 1);
  const facts = factsOf(node.data).map(described);
  const number = Object.entries(node.data).find(([, v]) => typeof v === "number");
  return { kind: "assertion", shape: shapeName(node.shape), title: shapeName(node.shape),
    headline: number ? described({ label: number[0], value: String(number[1]) }) : null,
    facts: facts.filter((f) => f.label !== number?.[0]).sort((x, y) => isEnum(x) - isEnum(y)), about: titleOf(node.about, a, titles), members: membersOf(a.nodes[node.about] ?? node, a, titles) };
}

export type StepView = { stage: string; ms: number; text: string };

// What each stage of a hub response's trace did, in the Design pages' words.
// See Design > Answer flow, and Design > HubRouter.
const STAGES = new Map([
  ["match", "Found the intents closest to the question in the intent index."],
  ["select", "Gave each matched hub a hub score and chose which hubs to dispatch."],
  ["hints", "Checked the question for pattern hints, codes that the hub's data confirms."],
  ["router", "Intent routing: one router model call picked an intent, or declined."],
  ["shadow", "Recorded whether pattern hints could have answered without intent routing. Never acted on."],
  ["price", "Checked your bid against the intent's ask."],
  ["resolve", "Parameter resolution: turned the extracted values into WarmHub data."],
  ["tool", "Ran the intent's tool in a tool sandbox."],
  ["validate", "Checked the answer against the intent's answer shapes."],
  ["warmhub", "The WarmHub calls that the hub made."],
]);

/** The trace of one hub response, in words, for "How this was answered". */
export function stepsOf(r: HubResponse): StepView[] {
  return r.trace.steps.map((s) => {
    // The warmhub stage has no time of its own; its calls carry it.
    const total = typeof s.detail?.totalMs === "number" ? s.detail.totalMs : s.ms;
    const calls = Array.isArray(s.detail?.calls) ? s.detail.calls.length : null;
    const base = STAGES.get(s.stage) ?? `The ${s.stage} stage.`;
    return { stage: s.stage, ms: total, text: calls !== null ? `${base} ${calls} ${calls === 1 ? "call" : "calls"}.` : base };
  });
}
